import { format } from "date-fns";
import {
  dayLabel,
  isUrgentEntry,
  parseApiDate,
  toDisplayDate,
  toTitleCase,
  type CalendarDay,
  type CalendarEntry,
} from "./alertCalendarData";
import { EventCard } from "./CalendarGrid";

function GroupHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 mt-4 text-[10.5px] font-extrabold tracking-wider d first:mt-1">
      {children}
    </p>
  );
}

/**
 * Day view. Review events and new alerts render as full cards; reminders stay
 * as quieter dashed rows, since they only mean "the window is still running".
 */
export function DayAgenda({
  day,
  onSelect,
}: {
  day: CalendarDay;
  onSelect: (entry: CalendarEntry, date: string) => void;
}) {
  const date = parseApiDate(day.date);
  const reviewDue = day.reviewDue ?? [];
  const newAlerts = day.newAlerts ?? [];
  const reminders = day.reminders ?? [];

  return (
    <div className="px-4 py-4 sm:px-5">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-2.5 border-b border-border/60 pb-3">
        <span className="text-[17px] font-bold text-foreground">
          {format(date, "EEEE d MMMM")}
        </span>
        <span className="text-[11.5px] tabular-nums d">
          {toDisplayDate(day.date)}
        </span>
      </div>

      {day.totalCount === 0 ? (
        <p className="py-11 text-center text-[12.5px] d">
          Nothing scheduled on this day.
        </p>
      ) : (
        <>
          {reviewDue.length > 0 && (
            <>
              <GroupHeading>
                Review due - {reviewDue.length} event
                {reviewDue.length > 1 ? "s" : ""}
              </GroupHeading>
              {reviewDue.map((entry) => (
                <EventCard
                  key={entry.documentId}
                  entry={entry}
                  large
                  onSelect={(e) => onSelect(e, day.date)}
                />
              ))}
            </>
          )}

          {newAlerts.length > 0 && (
            <>
              <GroupHeading>
                New alerts - {newAlerts.length} window
                {newAlerts.length > 1 ? "s" : ""} opening
              </GroupHeading>
              {newAlerts.map((entry) => (
                <EventCard
                  key={entry.documentId}
                  entry={entry}
                  large
                  onSelect={(e) => onSelect(e, day.date)}
                />
              ))}
            </>
          )}

          {reminders.length > 0 && (
            <>
              {/* <GroupHeading>
                Reminders - {reminders.length} in review window
              </GroupHeading> */}
              {reminders.map((entry) => (
                <button
                  key={entry.documentId}
                  type="button"
                  onClick={() => onSelect(entry, day.date)}
                  className="mb-2 flex w-full items-center gap-3 rounded-[9px] border border-dashed border-border bg-muted/30 px-3.5 py-2.5 text-left transition-colors hover:border-indigo-400 hover:bg-indigo-500/10"
                >
                  <span
                    className={`w-1 flex-none self-stretch rounded-sm opacity-50 ${isUrgentEntry(entry) ? "bg-amber-500" : "bg-blue-500"
                      }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12.5px] font-semibold text-foreground">
                      {entry.title}
                    </span>
                    <span className="mt-0.5 block text-[10.5px] d">
                      {toTitleCase(entry.documentType)} - {entry.orgName} -{" "}
                      {dayLabel(entry)}
                    </span>
                  </span>
                  <span className="flex-none text-[11px] d">
                    Review Date {toDisplayDate(entry.reviewDate)}
                  </span>
                  <span className="flex-none rounded-full bg-muted px-2 py-1 text-[9.5px] font-bold d">
                    {entry.daysRemaining}d left
                  </span>
                </button>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
